// /shifts/reports — Per-session shift reports with bills drill-down.
//
// One row per attendance session (unique attendance.id). Period presets
// (Today / Yesterday / This Week / Last 7 / This Month / Last 30) feed
// start_date/end_date filters. Aggregate KPI strip totals are computed
// server-side; the drill-down lazily fetches /attendance/:id/bills.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  ErrorBoundary,
  Select,
  Badge,
  Avatar,
  Skeleton,
  EmptyState,
  Button,
  DatePicker,
} from '@bbpos/ui';
import type { DateRange } from '@bbpos/ui';
import { locationService } from '@/services/location.service';
import { formatPaise } from '@/utils/currency';
import { useShiftReports, useSessionBills } from '@/features/shift-reports';
import type { AttendanceStatus, ShiftReportRow } from '@/features/shift-reports';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';

type Preset = 'today' | 'yesterday' | 'this_week' | 'last_7' | 'this_month' | 'last_30' | 'custom';

const PRESET_OPTIONS: { value: Preset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_7', label: 'Last 7 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_30', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom…' },
];

// Reports are constrained to the last 365 days. Anything earlier is disabled
// at the picker level so the cashier can't pick it; we also clamp at the
// API call layer for defence in depth.
function oneYearAgo(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - 365);
  return d;
}
function todayDate(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'PRESENT', label: 'Present' },
  { value: 'LATE', label: 'Late' },
  { value: 'ABSENT', label: 'Absent' },
  { value: 'HALF_DAY', label: 'Half Day' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'SCHEDULED', label: 'Scheduled' },
];

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function rangeForPreset(p: Preset): { start_date?: string; end_date?: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = toISODate(today);
  switch (p) {
    case 'today':
      return { start_date: end, end_date: end };
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      const ys = toISODate(y);
      return { start_date: ys, end_date: ys };
    }
    case 'this_week': {
      const d = new Date(today);
      const day = d.getDay(); // 0=Sun
      const monOffset = day === 0 ? -6 : 1 - day;
      d.setDate(d.getDate() + monOffset);
      return { start_date: toISODate(d), end_date: end };
    }
    case 'last_7': {
      const d = new Date(today);
      d.setDate(d.getDate() - 6);
      return { start_date: toISODate(d), end_date: end };
    }
    case 'this_month': {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start_date: toISODate(d), end_date: end };
    }
    case 'last_30': {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      return { start_date: toISODate(d), end_date: end };
    }
    case 'custom':
      // Custom presets pull their range from `customRange` state, not from here.
      return {};
  }
}

/** A clock time on `timeZone`'s clock (T14: a session shows its store's), else the viewer's. */
function formatTime(iso: string | null, timeZone?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone });
}

function statusVariant(status: AttendanceStatus): 'success' | 'danger' | 'warning' | 'info' | 'draft' {
  switch (status) {
    case 'PRESENT':
    case 'COMPLETED':
      return 'success';
    case 'ABSENT':
      return 'danger';
    case 'LATE':
      return 'warning';
    case 'HALF_DAY':
      return 'info';
    default:
      return 'draft';
  }
}

function rolePillTone(role: string): string {
  switch ((role || '').toUpperCase()) {
    case 'OWNER':
      return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300';
    case 'MANAGER':
      return 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300';
    case 'CASHIER':
      return 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300';
    default:
      return 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400';
  }
}

function KpiCard({ label, value, tone, icon }: { label: string; value: string; tone: string; icon: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 p-5 border border-neutral-100 dark:border-neutral-700">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${tone}`}>{icon}</div>
        <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">{label}</span>
      </div>
      <p className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums">{value}</p>
    </div>
  );
}

export function SessionBillsDrawer({ attendanceId, onClose, currency }: { attendanceId: string; onClose: () => void; currency: 'INR' }) {
  const billsQ = useSessionBills(attendanceId);
  const rows = billsQ.data ?? [];
  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-black/50 animate-fade-in" onClick={onClose}>
      <div
        className="w-full sm:w-[640px] bg-white dark:bg-neutral-900 h-full overflow-y-auto shadow-2xl animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between sticky top-0 bg-white dark:bg-neutral-900 z-10">
          <div>
            <h2 className="text-lg font-bold text-neutral-900 dark:text-white">Bills in this session</h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Session {attendanceId.slice(0, 8)}…</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">✕</Button>
        </div>
        <div className="p-5">
          {billsQ.isFetching && rows.length === 0 ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />)}
            </div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-sm text-neutral-500 dark:text-neutral-400">No bills rung in this session.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 border-b border-neutral-100 dark:border-neutral-700">
                  <th className="py-2 pr-3 font-semibold">Bill #</th>
                  <th className="py-2 pr-3 font-semibold">Time</th>
                  <th className="py-2 pr-3 font-semibold">Customer</th>
                  <th className="py-2 pr-3 font-semibold">Payment</th>
                  <th className="py-2 pr-3 font-semibold text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => (
                  <tr key={b.id} className="border-b border-neutral-50 dark:border-neutral-800 hover:bg-brand-50/40 dark:hover:bg-brand-900/10">
                    <td className="py-2.5 pr-3 font-mono text-xs">
                      <Link to={`/bills/${b.id}`} className="text-brand-600 hover:underline">{b.bill_number}</Link>
                    </td>
                    <td className="py-2.5 pr-3 text-xs text-neutral-600 dark:text-neutral-300 tabular-nums">{formatTime(b.created_at)}</td>
                    <td className="py-2.5 pr-3 text-xs text-neutral-700 dark:text-neutral-200">{b.customer_name ?? '—'}</td>
                    <td className="py-2.5 pr-3">
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                        {b.payment_method}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-right tabular-nums font-bold text-neutral-900 dark:text-neutral-100">
                      {formatPaise(b.grand_total, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export function ShiftReportsContent({ embedded = false }: { embedded?: boolean } = {}) {
  const currency = useTenantCurrency();
  const [preset, setPreset] = useState<Preset>('today');
  const [locationId, setLocationId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [openSessionId, setOpenSessionId] = useState<string | null>(null);
  // Custom date range — preset='custom' renders the DatePicker and uses this
  // state instead of the rangeForPreset helper. Defaults to "last 7 days".
  const [customRange, setCustomRange] = useState<DateRange>(() => {
    const to = todayDate();
    const from = new Date(to);
    from.setDate(from.getDate() - 6);
    return { from, to };
  });

  const minDate = useMemo(() => oneYearAgo(), []);
  const maxDate = useMemo(() => todayDate(), []);

  const { start_date, end_date } = useMemo(() => {
    if (preset !== 'custom') return rangeForPreset(preset);
    // Defence in depth: clamp the picked range to the 1-year window even if
    // somehow a date outside it slips through.
    const clampedFrom = customRange.from < minDate ? minDate : customRange.from;
    const clampedTo = customRange.to > maxDate ? maxDate : customRange.to;
    return { start_date: toISODate(clampedFrom), end_date: toISODate(clampedTo) };
  }, [preset, customRange, minDate, maxDate]);

  const { data: locationsData } = useQuery({
    queryKey: ['shift-reports-locations'],
    queryFn: () => locationService.list({ limit: 100, is_active: 'true' }),
    staleTime: 60_000,
  });

  const locationOptions = useMemo(
    () => [
      { value: '', label: 'All Locations' },
      ...(locationsData?.data ?? []).map((l) => ({ value: l.id, label: l.name })),
    ],
    [locationsData],
  );

  const reportQ = useShiftReports({
    start_date,
    end_date,
    ...(locationId ? { location_id: locationId } : {}),
    ...(status ? { status: status as AttendanceStatus } : {}),
    limit: 50,
  });

  const rows = reportQ.data?.data ?? [];
  const totals = reportQ.data?.totals;

  return (
    <div className="animate-fade-in">
      {!embedded && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 p-6 sm:p-8 text-white mb-6 shadow-lg">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm">
            Reports
          </span>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Shift Reports</h1>
          <p className="mt-1.5 text-sm sm:text-base text-white/90 max-w-2xl">
            Per-session billing summary — see exactly who was on duty, what they rang up, and how much cash they collected.
          </p>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-md ring-1 ring-neutral-100 dark:ring-neutral-700 p-4 mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-1.5">
            {PRESET_OPTIONS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setPreset(p.value)}
                className={[
                  'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
                  preset === p.value
                    ? 'bg-gradient-to-r from-brand-500 to-indigo-500 text-white shadow-sm'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-600',
                ].join(' ')}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="min-w-[200px]">
            <Select
              label="Location"
              value={locationId}
              onChange={(v) => setLocationId(Array.isArray(v) ? (v[0] ?? '') : v)}
              options={locationOptions}
            />
          </div>
          <div className="min-w-[180px]">
            <Select
              label="Status"
              value={status}
              onChange={(v) => setStatus(Array.isArray(v) ? (v[0] ?? '') : v)}
              options={STATUS_OPTIONS}
            />
          </div>
        </div>
        {preset === 'custom' && (
          <div className="mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-700">
            <div className="max-w-md">
              <DatePicker
                variant="range"
                value={customRange}
                onChange={(v) => setCustomRange(v)}
                minDate={minDate}
                maxDate={maxDate}
                presets={[]}
                label="Custom range"
                placeholder="Pick from / to date"
              />
              <p className="mt-1 text-[11px] text-neutral-500 dark:text-neutral-400">
                Choose a date from the last 1 year — earlier dates are disabled.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-5">
        <KpiCard
          label="Sessions"
          value={(totals?.sessions ?? 0).toLocaleString('en-IN')}
          tone="bg-gradient-to-br from-brand-500 to-indigo-500"
          icon={<span className="text-base">🕒</span>}
        />
        <KpiCard
          label="Hours"
          value={(totals?.hours ?? 0).toFixed(1)}
          tone="bg-gradient-to-br from-teal-500 to-emerald-500"
          icon={<span className="text-base">⏱</span>}
        />
        <KpiCard
          label="Bills"
          value={(totals?.bills ?? 0).toLocaleString('en-IN')}
          tone="bg-gradient-to-br from-violet-500 to-pink-500"
          icon={<span className="text-base">🧾</span>}
        />
        <KpiCard
          label="Sales"
          value={formatPaise(totals?.sales_paise ?? 0, currency)}
          tone="bg-gradient-to-br from-amber-500 to-orange-500"
          icon={<span className="text-base">💰</span>}
        />
        <KpiCard
          label="Cash Collected"
          value={formatPaise(totals?.cash_paise ?? 0, currency)}
          tone="bg-gradient-to-br from-success-500 to-emerald-600"
          icon={<span className="text-base">💵</span>}
        />
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-md ring-1 ring-neutral-100 dark:ring-neutral-700 overflow-hidden">
        {reportQ.isFetching && rows.length === 0 ? (
          <div className="p-5 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />)}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10">
            <EmptyState
              variant="page"
              title="No shift sessions in this period"
              description="Try a wider period preset or clear the location/status filters."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 border-b border-neutral-100 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/40">
                  <th className="py-3 px-3 font-semibold">Session</th>
                  <th className="py-3 px-3 font-semibold">Date</th>
                  <th className="py-3 px-3 font-semibold">Cashier</th>
                  <th className="py-3 px-3 font-semibold">Shift</th>
                  <th className="py-3 px-3 font-semibold">Location</th>
                  <th className="py-3 px-3 font-semibold">Clock In</th>
                  <th className="py-3 px-3 font-semibold">Clock Out</th>
                  <th className="py-3 px-3 font-semibold text-right">Hrs</th>
                  <th className="py-3 px-3 font-semibold text-right">Bills</th>
                  <th className="py-3 px-3 font-semibold text-right">Sales</th>
                  <th className="py-3 px-3 font-semibold text-right">Cash</th>
                  <th className="py-3 px-3 font-semibold">Status</th>
                  <th className="py-3 px-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r: ShiftReportRow) => (
                  <tr
                    key={r.id}
                    className="border-b border-neutral-50 dark:border-neutral-800 even:bg-neutral-50/40 dark:even:bg-neutral-900/30 hover:bg-brand-50/40 dark:hover:bg-brand-900/10 transition-colors"
                  >
                    <td className="py-2.5 px-3 font-mono text-[11px] text-neutral-500 dark:text-neutral-400" title={r.id}>
                      {r.id.slice(0, 8)}…
                    </td>
                    <td className="py-2.5 px-3 text-xs tabular-nums whitespace-nowrap">{r.shift_date}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Avatar name={r.user_name} size="sm" />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">{r.user_name}</p>
                          <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${rolePillTone(r.user_role)}`}>
                            {r.user_role}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-3">
                      <p className="text-sm text-neutral-800 dark:text-neutral-100">{r.shift_name}</p>
                      <p className="text-[10px] text-neutral-500 dark:text-neutral-400 tabular-nums">
                        {r.shift_start_time}–{r.shift_end_time}
                      </p>
                    </td>
                    <td className="py-2.5 px-3 text-xs text-neutral-700 dark:text-neutral-200">{r.location_name}</td>
                    <td className="py-2.5 px-3 text-xs tabular-nums font-mono">{formatTime(r.clock_in, r.timezone)}</td>
                    <td className="py-2.5 px-3 text-xs tabular-nums font-mono">
                      {r.clock_out
                        ? formatTime(r.clock_out, r.timezone)
                        : <span className="inline-flex items-center gap-1 text-success-600 dark:text-success-400 font-semibold"><span className="h-1.5 w-1.5 rounded-full bg-success-500 animate-pulse" />On shift</span>}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums font-semibold">
                      {r.hours_worked != null ? r.hours_worked.toFixed(2) : '—'}
                      {r.overtime_hours ? (
                        <span className="block text-[10px] font-semibold text-warning-600 dark:text-warning-400">
                          +{r.overtime_hours.toFixed(2)} OT{r.is_holiday ? ' · holiday' : ''}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums font-bold text-violet-700 dark:text-violet-300">
                      {r.bills_count}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums font-bold text-neutral-900 dark:text-neutral-100">
                      {formatPaise(r.total_sales_paise, currency)}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums font-bold text-success-700 dark:text-success-400">
                      {formatPaise(r.cash_collected_paise, currency)}
                    </td>
                    <td className="py-2.5 px-3">
                      <Badge variant={statusVariant(r.status)} size="sm" dot>
                        {r.status === 'HALF_DAY' ? 'Half Day' : r.status.charAt(0) + r.status.slice(1).toLowerCase()}
                      </Badge>
                    </td>
                    <td className="py-2.5 px-3">
                      <button
                        type="button"
                        onClick={() => setOpenSessionId(r.id)}
                        className="text-xs font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-400 hover:underline whitespace-nowrap"
                      >
                        View bills →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openSessionId && (
        <SessionBillsDrawer
          attendanceId={openSessionId}
          onClose={() => setOpenSessionId(null)}
          currency={currency as 'INR'}
        />
      )}
    </div>
  );
}

export function ShiftReportsPage() {
  return (
    <PageContainer title="Shift Reports" breadcrumbs={[{ label: 'Home', href: '/dashboard' }, { label: 'Shifts', href: '/shifts' }, { label: 'Reports' }]}>
      <ErrorBoundary resetKey="shift-reports">
        <ShiftReportsContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
